import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export type FilterOption<FieldType> = {
  fieldName: keyof FieldType;
  value: unknown;
};

export type UseFilterObject<FieldType> = {
  options: FilterOption<FieldType>[];
  setFilterOption: (newFilterOption: FilterOption<FieldType>) => void;
  removeFilterOption: (fieldName: keyof FieldType) => void;
  clearOptions: () => void;
  clearFilters: (excludeFields?: (keyof FieldType)[]) => void;
  getFilterObject: () => FieldType;
};

/**
 * Hook to manage filter state synchronized with URL query parameters.
 * Handles serialization/deserialization of filter values automatically.
 */
export const useFilter = <FieldType,>(
  defaultFilters: FilterOption<FieldType>[] = []
): UseFilterObject<FieldType> => {
  const [options, setOptions] = useState<FilterOption<FieldType>[]>([]);

  // Create a map of default values for quick comparison
  const defaultMap = defaultFilters.reduce((acc, { fieldName, value }) => {
    acc[fieldName as string] = value;
    return acc;
  }, {} as Record<string, unknown>);

  const navigate = useNavigate();
  const location = useLocation();

  // Helper to sync state changes to URL
  const setFilterDataToUrl = useCallback(
    (filterOptions: FilterOption<FieldType>[]) => {
      const urlSearchParams = new URLSearchParams(location.search);

      filterOptions.forEach(({ fieldName, value }) => {
        const key = fieldName as string;

        // Check if value equals default; if so, remove from URL to keep it clean
        const isDefault =
          JSON.stringify(value) === JSON.stringify(defaultMap[key]);

        if (!value || isDefault) {
          urlSearchParams.delete(key);
        } else {
          urlSearchParams.set(key, JSON.stringify(value));
        }
      });

      navigate(`?${urlSearchParams.toString()}`, { replace: true });
    },
    [location.search, navigate, defaultMap]
  );

  const removeFilterOptionFromUrl = useCallback(
    (fieldName: string) => {
      const urlSearchParams = new URLSearchParams(location.search);
      urlSearchParams.delete(fieldName);
      navigate(`?${urlSearchParams.toString()}`, { replace: true });
    },
    [location.search, navigate]
  );

  /**
   * Adds or updates a single filter option.
   * Automatically updates the URL query string.
   * @param newFilterOption - Object containing the field name and its new value.
   */
  const setFilterOption = useCallback(
    (newFilterOption: FilterOption<FieldType>) => {
      setOptions((oldOptions) => {
        const index = oldOptions.findIndex(
          ({ fieldName }) => fieldName === newFilterOption.fieldName
        );

        let newOptions: FilterOption<FieldType>[];

        if (index === -1) {
          newOptions = [...oldOptions, newFilterOption];
        } else {
          newOptions = oldOptions.map((filterOption) =>
            filterOption.fieldName === newFilterOption.fieldName
              ? newFilterOption
              : filterOption
          );
        }

        setFilterDataToUrl(newOptions);
        return newOptions;
      });
    },
    [setFilterDataToUrl]
  );

  /**
   * Removes a specific filter by its field name.
   * Updates state and removes the param from URL.
   * @param fieldName - The key of the filter to remove.
   */
  const removeFilterOption = useCallback(
    (fieldName: keyof FieldType) => {
      setOptions((oldOptions) => {
        const newFilterOptions = oldOptions.filter(
          (option) => option.fieldName !== fieldName
        );
        removeFilterOptionFromUrl(fieldName as string);
        return newFilterOptions;
      });
    },
    [removeFilterOptionFromUrl]
  );

  /**
   * Clear all filters except specified fields (safe bulk removal).
   * Used for "Clear All" buttons where some filters (like Search) might be preserved.
   * @param excludeFields - Array of field names to keep (e.g., ['searchingQuery'])
   */
  const clearFilters = useCallback(
    (excludeFields: (keyof FieldType)[] = []) => {
      setOptions((oldOptions) => {
        const urlSearchParams = new URLSearchParams(location.search);
        const fieldsToKeep: FilterOption<FieldType>[] = [];

        oldOptions.forEach((opt) => {
          if (excludeFields.includes(opt.fieldName)) {
            fieldsToKeep.push(opt);
          } else {
            // Mark for deletion from URL
            urlSearchParams.delete(opt.fieldName as string);
          }
        });

        // Perform one single navigation update to prevent race conditions
        navigate(`?${urlSearchParams.toString()}`, { replace: true });

        return fieldsToKeep;
      });
    },
    [location.search, navigate]
  );

  /**
   * Completely clears all active filters.
   * Alias for clearFilters([]).
   */
  const clearOptions = useCallback(() => {
    // Reusing clearFilters logic to avoid race conditions with multiple URL updates
    clearFilters([]);
  }, [clearFilters]);

  /**
   * Transforms the internal filter options array into a plain object.
   * Useful for passing filters directly to API requests or GraphQL variables.
   * @returns Record<FieldName, Value>
   */
  const getFilterObject = useCallback(() => {
    const filterObject = options.reduce((acc, current) => {
      return { ...acc, [current.fieldName]: current.value };
    }, {});

    return filterObject as FieldType;
  }, [options]);

  // Sync state from URL on mount or URL change
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const filters: FilterOption<FieldType>[] = [];

    const isUrlParamFilterValue = (value: string, key: string) => {
      const invalidValues = ['asc', 'desc'];
      const invalidKeys = ['page'];

      return (
        !invalidValues.includes(value.toLocaleLowerCase()) &&
        !invalidKeys.includes(key.toLocaleLowerCase())
      );
    };

    urlSearchParams.forEach((value, key) => {
      if (!isUrlParamFilterValue(value, key)) {
        return;
      }
      filters.push({
        fieldName: key as keyof FieldType,
        value: value ? JSON.parse(value) : value,
      });
    });

    if (filters.length !== 0) {
      setOptions(filters);
    } else {
      setOptions(defaultFilters);
    }
  }, [location.search]); // Removed defaultFilters to avoid infinite loops

  return {
    options,
    setFilterOption,
    removeFilterOption,
    clearOptions,
    clearFilters,
    getFilterObject,
  };
};
